import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveLine, UnarchiveFilled } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useApartmentPricesPageVM } from './useApartmentPricesPageVM';
import { Formik } from 'formik';
import { PriceType, PriceTypeToCreate } from '@services/member/list/price-types/price-types.types';

export default function ApartmentPricesPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchivePriceTypes,
        updatePriceType,
        createPriceType,
        MySwal,
        needReload,
        setIsArchived
    } = useApartmentPricesPageVM();
    const columnHelper = createColumnHelper<PriceType>();
    const navigate = useNavigate();

    const handleModificationPriceTypes = async (priceType: PriceTypeToCreate | null, id?: number) => {
        await MySwal.fire<PriceTypeToCreate>({
            title: priceType ? "Modification d'un type de prix d'appartement" : "Création d'un type de prix d'appartement",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: priceType ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ code: priceType?.code ?? '', label: priceType?.label ?? '', description: priceType?.description ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updatePriceType({
                                id: id,
                                code: values.code,
                                label: values.label,
                                description: values.description
                            });
                        } else {
                            createPriceType({
                                code: values.code,
                                label: values.label,
                                description: values.description
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-price-type" onSubmit={handleSubmit}>
                            <Input name="code" required label="Code" placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" required label="Libellé" placeholder="Libellé" value={values.label} onChange={handleChange} />
                            <Input name="description" label="Description" placeholder="Description" value={values.description} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-price-type') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('description', {
            header: 'Description',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationPriceTypes(
                                    { code: info.row.original.code, label: info.row.original.label, description: info.row.original.description },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchivePriceTypes([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<PriceType>[];

    return (
        <>
            <div className="settings-tabs-content service-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/price-types/${row.id}`)}
                    defaultSorting={{
                        field: 'label',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    onMetadata={handleMetadata}
                    createButtonOnClick={() => handleModificationPriceTypes(null)}
                    createButtonLabel="Créer un type de prix d'appartement"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    deleting={{
                        enabled: isArchived,
                        entity: 'apartment_price_type',
                    }}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: PriceType[]) => handleArchivePriceTypes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
